import React from 'react';
import { motion } from 'framer-motion';
import { RotateCcw, ShieldCheck, Clock, PackageCheck, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const WHATSAPP_NUMBER = '918279765078';

const ReturnPolicy = () => {
  const points = [
    {
      icon: Clock,
      title: '7-Day Return Window',
      text: 'You can request a return within 7 days of receiving your order if the product is unopened and in its original packaging.'
    },
    {
      icon: PackageCheck,
      title: 'Eligible Products',
      text: 'Sealed and unused health kits are eligible for return. For hygiene and safety reasons, opened or partially used kits cannot be returned.'
    },
    {
      icon: ShieldCheck,
      title: 'Damaged or Wrong Items',
      text: 'If you receive a damaged, defective or incorrect product, we will arrange a free replacement or full refund. Please share a photo within 48 hours of delivery.'
    },
    {
      icon: RotateCcw,
      title: 'Refund Process',
      text: 'Once your return is received and inspected, refunds are processed to the original payment method within 5-7 business days.'
    }
  ];

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="return-policy-page">
      {/* Hero */}
      <section className="bg-primary/5 py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-4"
          >
            <h1 className="text-5xl sm:text-6xl font-bold font-serif">Return Policy</h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Your satisfaction matters to us. Here&apos;s everything you need to know about returns and refunds at Aatreya Herbals.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <div className="max-w-4xl mx-auto">
          <div className="grid sm:grid-cols-2 gap-6 mb-12">
            {points.map((point, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-card rounded-2xl border border-border p-6 shadow-lg"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <point.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-serif font-semibold text-xl mb-2">{point.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{point.text}</p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-2xl border border-border p-8 shadow-lg space-y-4 mb-12"
          >
            <h2 className="text-2xl font-serif font-bold">How to Request a Return</h2>
            <ol className="space-y-3 text-muted-foreground">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">1</span>
                <span>Contact us on WhatsApp or email within 7 days of delivery with your order details.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">2</span>
                <span>Share your order ID and reason for return (with photos if the item is damaged).</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-bold">3</span>
                <span>Our team will guide you through the pickup/return process and confirm your refund.</span>
              </li>
            </ol>
            <p className="text-sm text-muted-foreground pt-2">
              <strong>Note:</strong> Consultation fees for booked appointments are non-refundable once the consultation is completed.
            </p>
          </motion.div>

          <div className="bg-primary rounded-2xl p-8 text-center">
            <MessageCircle className="w-12 h-12 text-primary-foreground mx-auto mb-4" />
            <h3 className="text-2xl font-serif font-bold text-primary-foreground mb-2">
              Need to Return an Order?
            </h3>
            <p className="text-primary-foreground/90 mb-6">
              Our support team is here to help you on WhatsApp
            </p>
            <Button
              onClick={() =>
                window.open(
                  `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent('Hello Aatreya Herbals! I want to request a return for my order.')}`,
                  '_blank'
                )
              }
              size="lg"
              variant="secondary"
              data-testid="return-whatsapp-button"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Request Return on WhatsApp
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReturnPolicy;
